import { PrimaryButton } from "@mnk/frontend/components/ui/button/PrimaryButton";
import { trpc } from "@mnk/frontend/lib/trpc";
import { useState } from "react";

interface Props {
  onClose: () => void;
  officeId: string;
  parentId?: string;
  afterCreateItem: () => void;
}

export const NewProjectModal = ({ onClose, officeId, parentId, afterCreateItem }: Props) => {
  const [name, setName] = useState("");
  const [fiscalYear, setFiscalYear] = useState("");
  const [workTitle, setWorkTitle] = useState("");
  const [workDetail, setWorkDetail] = useState("");
  const [drawingType, setDrawingType] = useState<"設計図" | "変更図" | "">("");
  const [workReiwaYear, setWorkReiwaYear] = useState("");
  const [workMonth, setWorkMonth] = useState("");
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const itemMutation = trpc.createItem.useMutation();

  const onSubmit = async () => {
    setProcessing(true);
    try {
      await itemMutation.mutateAsync({
        itemType: "project",
        name,
        officeId,
        parentId,
        fiscalYear: fiscalYear || null,
        workTitle: workTitle || null,
        workDetail: workDetail || null,
        drawingType: drawingType || null,
        workYearMonth: workReiwaYear && workMonth ? `令和${workReiwaYear}年${workMonth}月` : null,
      });
      afterCreateItem();
      onClose();
    } catch {
      setError("プロジェクトの作成に失敗しました");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 flex justify-center items-center z-50"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
    >
      <div className="w-full h-full flex justify-center items-center">
        <div
          className="bg-white rounded-lg shadow-lg w-[480px] flex flex-col max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === "Escape") onClose();
          }}
        >
          <div className="bg-blue-400 rounded-t-lg flex items-center p-2 h-10 flex-shrink-0">
            <h2 className="text-center text-md font-bold text-white flex-1">プロジェクトを作成</h2>
            <button type="button" className="text-white hover:text-blue-100 text-lg leading-none" onClick={onClose}>
              ✕
            </button>
          </div>
          <div className="p-4 flex flex-col gap-4 rounded-b-lg">
            <div className="flex flex-col gap-1">
              <label htmlFor="name" className="text-sm text-gray-500">
                プロジェクト名 <span className="text-red-500">*</span>
              </label>
              <input
                id="name"
                type="text"
                className="border border-gray-300 rounded-md p-2"
                placeholder="プロジェクト名を入力"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className="border-t border-gray-200 pt-3">
              <p className="text-xs text-gray-400 mb-3">表紙情報（任意）</p>
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1">
                  <label htmlFor="fiscalYear" className="text-sm text-gray-500">
                    作業年度
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">令和</span>
                    <input
                      id="fiscalYear"
                      type="text"
                      inputMode="numeric"
                      className="border border-gray-300 rounded-md p-2 w-20"
                      placeholder="8"
                      value={fiscalYear}
                      onChange={(e) => setFiscalYear(e.target.value.replace(/[^0-9]/g, ""))}
                    />
                    <span className="text-sm text-gray-500">年度</span>
                  </div>
                </div>

                <div className="flex flex-col gap-1">
                  <label htmlFor="workTitle" className="text-sm text-gray-500">
                    業務タイトル
                  </label>
                  <input
                    id="workTitle"
                    type="text"
                    className="border border-gray-300 rounded-md p-2"
                    placeholder="例：京浜管内道路保全工事業務"
                    maxLength={40}
                    value={workTitle}
                    onChange={(e) => setWorkTitle(e.target.value)}
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label htmlFor="workDetail" className="text-sm text-gray-500">
                    工事名詳細
                  </label>
                  <input
                    id="workDetail"
                    type="text"
                    className="border border-gray-300 rounded-md p-2"
                    placeholder="例：植栽作業"
                    maxLength={40}
                    value={workDetail}
                    onChange={(e) => setWorkDetail(e.target.value)}
                  />
                </div>

                <fieldset className="flex flex-col gap-1">
                  <legend className="text-sm text-gray-500">設計図・変更図の選択</legend>
                  <div className="flex items-center gap-4">
                    {(["設計図", "変更図"] as const).map((type) => (
                      <label key={type} className="flex items-center gap-1 cursor-pointer text-sm">
                        <input
                          type="radio"
                          name="drawingType"
                          value={type}
                          checked={drawingType === type}
                          onChange={() => setDrawingType(type)}
                        />
                        {type}
                      </label>
                    ))}
                    {drawingType && (
                      <button
                        type="button"
                        className="text-xs text-gray-400 underline"
                        onClick={() => setDrawingType("")}
                      >
                        クリア
                      </button>
                    )}
                  </div>
                </fieldset>

                <div className="flex flex-col gap-1">
                  <label htmlFor="workReiwaYear" className="text-sm text-gray-500">
                    作業年月
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">令和</span>
                    <input
                      id="workReiwaYear"
                      type="number"
                      min="1"
                      className="border border-gray-300 rounded-md p-2 w-20"
                      placeholder="8"
                      value={workReiwaYear}
                      onChange={(e) => setWorkReiwaYear(e.target.value)}
                    />
                    <span className="text-sm text-gray-500">年</span>
                    <select
                      className="border border-gray-300 rounded-md p-2"
                      value={workMonth}
                      onChange={(e) => setWorkMonth(e.target.value)}
                    >
                      <option value="">--</option>
                      {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                        <option key={m} value={String(m)}>
                          {m}
                        </option>
                      ))}
                    </select>
                    <span className="text-sm text-gray-500">月</span>
                  </div>
                </div>
              </div>
            </div>

            {error && <p className="text-sm text-red-500 text-center">{error}</p>}

            <PrimaryButton
              className="w-full"
              onClick={async (e) => {
                e.preventDefault();
                await onSubmit();
              }}
              disabled={processing || !name.trim()}
            >
              {processing ? "作成中..." : "作成"}
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
};
